// Dependencies
import React from 'react';
import { connect } from 'react-redux';

import AccessCodeForm from './accessCode/AccessCodeForm.jsx';
import EventInfos from './EventInfos.jsx';
import EventNotes from './EventNotes.jsx';
import CategoryZone from './categories/CategoryZone.tsx';
import Button from '../../components/button/Button.tsx';
import ZigZagBox from '../../components/zig-zag-box/ZigZagBox.jsx';
import Spinner from '../../components/spinner/Spinner.jsx';
import HelpInfo from './HelpInfo.jsx';
import CustomInfoBox from './CustomInfoBox.jsx';
import SaleNotPossibleInfo from './SaleNotPossibleInfo.jsx';

import eventService from '../../lib/event-service';

import Errors from '../../components/errors/Errors';
import ButtonEventsOverview from '../../components/button-events-overview/ButtonEventsOverview.jsx';

import { batchUpdateBulkTickets } from '../../redux/shoppingCartReducer';
import { ThemeContext } from '../../components/theme/Theme.jsx';

// Styles
import './EventPage.scss';
const defaultProps = {
	event: {
		title: '',
		subtitle: '',
		mainImageSrc: '',
		begin: '',
		venueId: '',
		categories: [],
		imagesMetadataIds: []
	},
	venue: {
		name: '',
	},
	bookingOffice: {
		activShipmentTypes: [],
		shipmentFees: [],
		shipmentTypeAvailabilityInHours: [],
		activPaymentTypes: [],
		paymentTypeAvailabilityInHours: [],
	},
	errors: [],
};

class EventPage extends React.Component {
	static contextType = ThemeContext;
	constructor(props) {
		super(props);
		this.state = {
			enteringAccessCode: false,
		};
	}

	render() {
		this.propsWithDefaults = Object.assign({},defaultProps,this.props)
		const anyShipmentPaymentPossible = eventService.isAnyShipmentAndPaymentPossible(this.propsWithDefaults.event, this.propsWithDefaults.bookingOffice);

		const saleIsPossible = eventService.isSalePossible(
			this.propsWithDefaults.event, this.propsWithDefaults.volatileTicketAvailability,
			this.propsWithDefaults.selectedTicketsByPriceLevelUuid,
			anyShipmentPaymentPossible
		);

		const style = {
			buttonContainer: {
				marginTop: '3em',
			},
		};

		const showAccessCodesForm = this.propsWithDefaults.accessCodesRequired || this.state.enteringAccessCode;

		return (
			<div className="event">
				{(this.propsWithDefaults.isLoading || this.propsWithDefaults.processingTickets) && <Spinner />}
				<Errors
					errors={this.propsWithDefaults.errors}
				/>
				<EventInfos
					imageMetaData={this.propsWithDefaults.imageMetaData}
					title={this.propsWithDefaults.event.title}
					subtitle={this.propsWithDefaults.event.subtitle}
					date={this.propsWithDefaults.event.begin}
					location={this.propsWithDefaults.venue.name}
					description={this.propsWithDefaults.event.description}
				/>
				<CustomInfoBox textPoperty="additionalEventInfo" />

				{
					!saleIsPossible
					&& <SaleNotPossibleInfo
						lifeCyclePhase={this.propsWithDefaults.event.lifecyclePhase}
						categoriesAreAvailable={(this.propsWithDefaults.event.categories && (this.propsWithDefaults.event.categories.length > 0))}
						ticketsAreAvailable={(this.propsWithDefaults.event.availableTickets > 0)}
						startOfSale={this.propsWithDefaults.event.startOfSale}
						shipmentPaymentPossible={anyShipmentPaymentPossible}
					/>
				}
				{
					saleIsPossible
					&& <div>
						<ZigZagBox>
							{showAccessCodesForm
								? <AccessCodeForm
									codeMandatory={this.propsWithDefaults.accessCodesMandatory}
									onLeave={() => this.setState({ enteringAccessCode: false })}
								/>
								: <div>
									<EventNotes eventNotes={this.propsWithDefaults.event.eventNotes} />
									<CategoryZone categories={this.propsWithDefaults.event.categories} />
								</div>
							}
						</ZigZagBox>
						<Button
							name={'addToShoppingCart'}
							buttonText={'In den Warenkorb'}
							btnStyle={'highlight'}
							buttonContainerStyle={style.buttonContainer}
							iconName={'shopping-cart'}
							onClick={() => this.propsWithDefaults.onClickInShoppingcart(this.propsWithDefaults.selectedTicketsByPriceLevelUuid)}
							disabled={showAccessCodesForm}
						/>
						{this.propsWithDefaults.accessCodesEnabled && !this.propsWithDefaults.accessCodesMandatory &&
						<Button
							name={'Aktionscode eingeben'}
							buttonText={'Aktionscode eingeben'}
							btnStyle={'highlight'}
							buttonContainerStyle={style.buttonContainer}
							iconName={'key'}
							onClick={() => { this.setState({ enteringAccessCode: true }); }}
							disabled={showAccessCodesForm}
						/>}
						<ButtonEventsOverview
							btnText={this.context.themeManager.getThemeTextOrHardcodedDefault('goToEventOverviewBtnLabel')}
							buttonContainerStyle={style.buttonContainer}
							iconName="angles-left"
						/>
					</div>
				}
				<HelpInfo />
			</div>
		);
	}
}


// ------------------------------
// Store Connection
// ------------------------------
const constructErrorMessages = (errors, shoppingCartError) => {
	const messages = [];
	if (shoppingCartError) {
		messages.push({
				message: `Es sind nicht so viele Karten verfügbar wie Sie ausgewählt haben.	Bitte überprüfen Sie Ihre Auswahl.`,
				elementIdToLink: null
			});
	}
	for (const err of errors) {
		if (err === 'noTicketsSelected') {
			messages.push({
				message: 'Bitte wählen Sie die gewünschte Anzahl an Tickets aus.',
				elementIdToLink: null
			});
		}
	}
	return [...messages];
};

const mapStateToProps = (state) => {
	const errors = constructErrorMessages(state.eventPage.errors, state.eventPage.shoppingCartError);
	const event = state.event.elements[state.event.selected] || {};
	const priceLevelUuids = [];
	event.categories.forEach((category) => {
		priceLevelUuids.push(category.standardPriceLevel.priceLevelUuid);
		// sort reduced pricelevel by pos attribute before pushing into priceLEvelUuids
		const sortedReducedPricelevels = Object.assign([], category.reducedPriceLevels).sort((a, b) => {
			if ((a.pos === null) && (b.pos === null)) {
				return 0;
			}
			return a.pos > b.pos ? 1 : -1;
		});
		sortedReducedPricelevels.forEach((priceLevel) => {
			priceLevelUuids.push(priceLevel.priceLevelUuid);
		});
	});
	const selectedTicketsByPriceLevelUuid = state.eventPage.amountOfTickets;
	Object.keys(selectedTicketsByPriceLevelUuid).forEach((key) => {
		if (!priceLevelUuids.includes(key)) {
			delete selectedTicketsByPriceLevelUuid[key];
		}
	});
	const volatileTicketAvailability = state.eventPage.volatileTicketAvailability;
	const processingTickets = state.eventPage.processingTickets;

	const imageMetaData = (event.imagesMetadataIds && event.imagesMetadataIds.length > 0) ?
		state.file.elements[event.imagesMetadataIds[0]] : {};

	const accessCodesEnabled = state.accessCodes.accessCodesEnabled;
	const accessCodesMandatory = state.accessCodes.accessCodesMandatory;
	const accessCodesPlUuidCodeMap = state.accessCodes.plUuidCodeMap;

	const accessCodesRequired = accessCodesEnabled && accessCodesMandatory &&
		(Object.keys(accessCodesPlUuidCodeMap).length === 0);

	return {
		isLoading: state.event.isLoading,
		orderIsLoading: state.order.isLoading,
		venue: state.venue.elements[event.venueId],
		event,
		imageMetaData,
		bookingOffice: state.bookingOffice,
		selectedTicketsByPriceLevelUuid,
		volatileTicketAvailability,
		processingTickets,
		accessCodesRequired,
		accessCodesMandatory,
		accessCodesEnabled,
		errors,
	};
};

const mapDispatchToProps = (dispatch) => ({
	onClickInShoppingcart: async (selectedTicketsByPriceLevelUuid) => {
		await dispatch(batchUpdateBulkTickets(selectedTicketsByPriceLevelUuid));
	},
});

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(EventPage);
